// src/belediye/components/ApplicationActionPanel.js
import { useCallback, useEffect, useState } from "react";
// FAZ-13: izin cutover'ı
import usePermissions from "../hooks/usePermissions";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplicationDetails } from "../redux/slices/belediyeApplicationDetailsSlice";

import FinalStatusStep from "./steps/FinalStatusStep";
import PersonnelStep from "./steps/PersonnelStep";
import ReviewOrForwardStep from "./steps/ReviewOrForwardStep";

//   SMS Panel (slice-based)
import ApplicationSmsPanel from "./steps/ApplicationSmsPanel";

import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  DocumentMagnifyingGlassIcon,
  TrashIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

import {
  fetchCloseRequestForApplication, //   NEW
} from "../redux/slices/belediyeCloseApprovalSlice";

import "./ApplicationActionPanel.css";
import "./ApplicationActionPanel.mobile.css";

/* -------------------------------------------------------------
   STEP LİSTESİ
------------------------------------------------------------- */
const stepList = [
  {
    key: "reviewOrForward",
    label: "İnceleme",
    icon: ClipboardDocumentListIcon,
    component: ReviewOrForwardStep,
  },
  {
    key: "personnel",
    label: "Personel",
    icon: UserGroupIcon,
    component: PersonnelStep,
  },
  {
    key: "finalStatus",
    label: "Sonuç",
    icon: CheckCircleIcon,
    component: FinalStatusStep,
  },
  {
    key: "summary",
    label: "Özet",
    icon: DocumentMagnifyingGlassIcon,
    component: () => (
      <p style={{ padding: 20, opacity: 0.6 }}>Özet ekranı henüz hazırlanmadı.</p>
    ),
  },
];

const ApplicationActionPanel = ({ applicationId, unitSlug, onDelete }) => {
  // FAZ-13: izin cutover'ı (uiOn=false iken eski rol yolu birebir)
  const { uiOn, can } = usePermissions();
  const dispatch = useDispatch();
  const { applicationData, loading, error } = useSelector(
    (state) => state.belediyeApplicationDetails
  );

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const appIdNum = Number(applicationId) || 0;

  //   NEW: pending close request var mı?
  const pendingCloseReq = useSelector(
    (state) =>
      state?.belediyeCloseApproval?.byApplicationId?.[String(appIdNum)]?.current || null
  );
  const hasPendingCloseReq = String(pendingCloseReq?.status || "") === "pending";

  const [activeStep, setActiveStep] = useState(0);
  const [isSmsOpen, setIsSmsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [panelData, setPanelData] = useState({
    reviewType: null,
    forwardUnitId: null,
    forwardNote: "",
    personnel: [],
    responderId: null,
    assignmentNote: "",
    statusNote: "",
    finalStatus: null,
  });

  /* -------------------------------------------------------------
     BAŞVURU DETAYINI ÇEK
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!applicationData || applicationData.id !== Number(applicationId)) {
      dispatch(
        fetchApplicationDetails({
          id: applicationId,
          token: localStorage.getItem("accessToken"),
        })
      );
    }
  }, [dispatch, applicationId]); // intentionally not adding applicationData

  /* -------------------------------------------------------------
       NEW: pending close request’i ısıt (talep var mı yok mu bilelim)
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!appIdNum) return;
    dispatch(fetchCloseRequestForApplication({ applicationId: appIdNum }));
  }, [dispatch, appIdNum]);

  /* -------------------------------------------------------------
     BAŞLANGIÇ ADIMINI STATÜYE GÖRE AYARLA
       İSTEK: Talep varsa -> Final step (2) açılsın.
     Talep yoksa -> mevcut davranış (işleme_alındı => Personel)
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!applicationData) return;

    // forwarded ise her zaman 0 kalsın (mevcut davranışı bozmayalım)
    if (applicationData.was_forwarded) {
      setActiveStep(0);
      return;
    }

    //   Talep varsa (pending close request) -> direkt Final step
    if (hasPendingCloseReq) {
      setActiveStep(2);
      return;
    }

    // Talep yoksa: eski mantık
    if (applicationData.status === "işleme_alındı") {
      setActiveStep(1);
    } else if (
      applicationData.status === "çözüldü" ||
      applicationData.status === "çözülemedi"
    ) {
      setActiveStep(2);
    } else {
      // eski davranış: başka durumlarda 0'da kalır
      setActiveStep(0);
    }
  }, [applicationData, hasPendingCloseReq]);

  /* -------------------------------------------------------------
     PANEL DATA GÜNCELLE
  ------------------------------------------------------------- */
  const updatePanelData = (updates) => {
    setPanelData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  /* -------------------------------------------------------------
     ADIM DEĞİŞTİRME
  ------------------------------------------------------------- */
  const goToStep = useCallback((stepIdx) => {
    setActiveStep(stepIdx);
  }, []);

  const handleNext = () => {
    const isForwardedNow =
      panelData.reviewType === "forward" || applicationData?.was_forwarded;
    if (isForwardedNow) return;

    if (activeStep < stepList.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  /* -------------------------------------------------------------
     SMS PANEL KAPATMA (ESC + backdrop)
  ------------------------------------------------------------- */
  useEffect(() => {
    if (!isSmsOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsSmsOpen(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isSmsOpen]);

  /* -------------------------------------------------------------
     GENEL DURUMLAR (STATÜYE GÖRE KISITLAR)
  ------------------------------------------------------------- */
  if (loading) return <p>Yükleniyor...</p>;
  if (error) return <p style={{ color: "red" }}>Hata: {error}</p>;
  if (!applicationData) return <p>Başvuru yüklenemedi.</p>;

  const status = applicationData.status;
  const isForwarded =
    panelData.reviewType === "forward" || applicationData?.was_forwarded;
  const isFinalized = status === "çözüldü" || status === "çözülemedi";
  const isReviewed = status === "işleme_alındı" || isFinalized;

  const StepComponent = stepList[activeStep].component;

  /* -------------------------------------------------------------
     RENDER
  ------------------------------------------------------------- */
  return (
    <div className="application-action-panel">
      {/* ---------------------------------------------------------
         SMS BAR (üstte chip tarzı tetikleyici)
      --------------------------------------------------------- */}
      <div className="aap-sms-bar">
        <button
          type="button"
          className="aap-sms-trigger"
          onClick={() => setIsSmsOpen(true)}
        >
          <ChatBubbleLeftRightIcon className="aap-sms-icon" />
          <span>Vatandaşa SMS Gönder</span>
        </button>
      </div>

      {/* ---------------------------------------------------------
         STEPLERİN LAYOUT'U (Sol: sidebar / Sağ: içerik)
      --------------------------------------------------------- */}
      <div className="aap-steps-container">
        {/* SIDEBAR */}
        <div className="aap-steps-sidebar">
          {stepList.map((step, idx) => {
            const Icon = step.icon;

            let disabledByFlow = false;

            if (isForwarded && idx > 0) {
              disabledByFlow = true;
            } else {
              if (idx === 1 || idx === 2) {
                disabledByFlow = !isReviewed;
              }
              if (idx === 3) {
                disabledByFlow = !isFinalized;
              }
            }

            const isActive = activeStep === idx;

            return (
              <button
                key={step.key}
                type="button"
                className={[
                  "aap-step-item",
                  isActive ? "active" : "",
                  disabledByFlow ? "disabled" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  if (!disabledByFlow) setActiveStep(idx);
                }}
              >
                <Icon className="aap-step-icon" />
                <div className="aap-step-text">{step.label}</div>
              </button>
            );
          })}

          {/* SİLME ALANI — FAZ-13 (3.1): uiOn'da applications.delete izni
              (matris: BŞK+MDR — M-03; backend destroy FAZ-08'den beri gate'li).
              uiOn=false: eski başkan-only görünürlük birebir. */}
          {(uiOn ? can("applications.delete") : user?.role === "başkan") && onDelete && (
            <button
              type="button"
              className="aap-delete-wrapper"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <TrashIcon className="aap-delete-icon" />
              <span>Başvuruyu Sil</span>
            </button>
          )}
        </div>

        {/* İÇERİK ALANI */}
        <div className="aap-step-content">
          {isForwarded && activeStep > 0 ? (
            <div className="aap-disabled-message">
              Başvuru yönlendirildiği için diğer adımlar devre dışı kaldı.
              <br />
              Lütfen yalnızca yönlendirme adımını kullanın.
            </div>
          ) : (
            <StepComponent
              applicationId={applicationId}
              applicationData={applicationData}
              unitSlug={unitSlug}
              panelData={panelData}
              updatePanelData={updatePanelData}
              onNext={handleNext}
              goToStep={goToStep}
              isFirstStep={activeStep === 0}
              isLastStep={activeStep === stepList.length - 1}
            />
          )}
        </div>
      </div>

      {/* ---------------------------------------------------------
           SMS OVERLAY (ActionPanel alanını tamamen kaplar)
      --------------------------------------------------------- */}
      {isSmsOpen && (
        <div
          className="aap-sms-overlay"
          onMouseDown={(e) => {
            // backdrop click closes
            if (e.target?.classList?.contains("aap-sms-overlay"))
              setIsSmsOpen(false);
          }}
        >
          <div className="aap-sms-sheet" onMouseDown={(e) => e.stopPropagation()}>
            <ApplicationSmsPanel
              applicationId={applicationId}
              applicationData={applicationData}
              onClose={() => setIsSmsOpen(false)}
              inlineMode={true}
            />
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------
         DELETE CONFIRM MODAL
      --------------------------------------------------------- */}
      {showDeleteConfirm && (
        <div
          className="aap-delete-modal-backdrop"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div className="aap-delete-modal" onClick={(e) => e.stopPropagation()}>
            <TrashIcon className="aap-delete-modal-icon" />

            <h3 className="aap-delete-modal-title">Başvuruyu Silmek Üzeresiniz</h3>

            <p className="aap-delete-modal-text">
              Bu işlem geri alınamaz. Başvuruyu silmek istediğinize emin misiniz?
            </p>

            <div className="aap-delete-modal-actions">
              <button
                className="aap-delete-button-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Vazgeç
              </button>

              <button
                className="aap-delete-button-primary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  onDelete();
                }}
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationActionPanel;